import React, { useEffect, useState } from 'react';
import { Loader2, LayoutGrid, Film, ImageOff, AlertTriangle, RefreshCw } from 'lucide-react';
import { resolveDownloadUrl } from '../../services/storageService';
import InfoHint from './InfoHint';
import Tooltip from '../ui/Tooltip';
import { TYPE } from '../../constants/typeScale';
import type { StoryboardShot } from '../../services/studioService';
import { qualityMiss } from './qualityMiss';

interface Props {
  shots: StoryboardShot[];
  previewing: boolean;
  /** Kick off (or refresh) the cheap keyframe preview. */
  onPreview: (force: boolean) => void;
  disabled?: boolean;
  /** Shots (0-based) whose panel the last preview run could not draw. Marked in words on the strip. */
  failedIndexes?: number[];
  /** Draw ONE panel again (charged like any panel) — offered when the judge says it may not match the cast. */
  onRedraw?: (index: number) => void;
}

/**
 * PreviewStrip — a comic-strip of CHEAP keyframes generated before the expensive video render (the propose→approve
 * gate). Each shot becomes a still panel so the creator can sanity-check framing/look/casting BEFORE spending on the
 * full clips. Craft stays hidden: it's just "see it as a comic strip first." Panels missing a preview show a placeholder.
 */
const PreviewStrip: React.FC<Props> = ({ shots, previewing, onPreview, disabled, failedIndexes = [], onRedraw }) => {
  const [urls, setUrls] = useState<Record<string, string>>({}); // keyed by storage PATH so a refreshed frame re-resolves
  const [failed, setFailed] = useState<Record<string, boolean>>({}); // a preview whose URL couldn't resolve (broken, not un-made)

  // Resolve preview thumbnails by PATH (a refreshed preview has a new path → re-resolves, no stale image).
  useEffect(() => {
    let alive = true;
    shots.forEach((s) => {
      const p = s.previewFramePath;
      if (p && !urls[p] && !failed[p]) {
        resolveDownloadUrl(p)
          .then((u) => { if (alive) setUrls((prev) => ({ ...prev, [p]: u })); })
          .catch(() => { if (alive) setFailed((prev) => ({ ...prev, [p]: true })); });
      }
    });
    return () => { alive = false; };
  }, [shots, urls, failed]);

  const anyPreview = shots.some((s) => s.previewFramePath);

  return (
    <div data-testid="preview-strip" className="mb-4 rounded-xl border border-secondary-200 dark:border-secondary-800 bg-secondary-50/50 dark:bg-secondary-800/30 p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className={`flex items-center gap-1.5 ${TYPE.label} text-secondary-400 dark:text-secondary-500`}>
          <LayoutGrid className="h-3.5 w-3.5" /> Preview
          <InfoHint text="See your episode as a quick comic strip before you generate the full video. A panel costs a small fraction of rendering that shot as video, and updating one only redraws what you changed — so you can try things until the episode reads right, then pay for the full render once." />
        </span>
        {/* ALWAYS the cheap path (force = false). It used to force a full redraw whenever any panel existed, so
            changing one line in a six-shot episode re-charged all six. The server now redraws what is missing or
            out of date and skips the rest for free, which is what makes a cheap rung worth climbing twice. */}
        <Tooltip content={anyPreview
          ? 'Redraw only the shots you have changed since these panels were made. Unchanged panels cost nothing.'
          : 'Draw one still per shot so you can check the framing, the look and the cast before rendering any video.'}
        >
          <button
            type="button"
            onClick={() => onPreview(false)}
            disabled={previewing || disabled}
            className={`ml-auto inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-1.5 ${TYPE.control} font-semibold text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-800 disabled:opacity-50`}
          >
            {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LayoutGrid className="h-3.5 w-3.5" />}
            {previewing ? 'Previewing…' : anyPreview ? 'Update preview' : 'Preview the episode'}
          </button>
        </Tooltip>
      </div>

      <div className="flex items-stretch gap-2 overflow-x-auto">
        {shots.map((shot, i) => {
          const url = shot.previewFramePath ? urls[shot.previewFramePath] : null;
          // A panel the last run could not draw, with nothing older to show in its place.
          const notDrawn = !url && !previewing && failedIndexes.includes(i);
          return (
            <div key={i} className="w-[132px] shrink-0">
              <div className="flex aspect-video items-center justify-center overflow-hidden rounded-lg border border-secondary-200 dark:border-secondary-800 bg-secondary-100 dark:bg-secondary-800">
                {url ? (
                  <img src={url} alt={`Shot ${i + 1} preview`} className="h-full w-full object-cover" />
                ) : notDrawn ? (
                  <span className={`flex flex-col items-center gap-1 ${TYPE.micro} text-secondary-500 dark:text-secondary-400`} data-testid={`preview-failed-${i + 1}`}>
                    <ImageOff className="h-4 w-4" aria-hidden />
                    Could not draw
                  </span>
                ) : shot.previewFramePath && failed[shot.previewFramePath] ? (
                  <ImageOff className="h-4 w-4 text-secondary-400" />
                ) : shot.previewFramePath && !previewing ? (
                  <Loader2 className="h-4 w-4 animate-spin text-secondary-400" />
                ) : previewing ? (
                  <Loader2 className="h-4 w-4 animate-spin text-secondary-400" />
                ) : (
                  <Film className="h-4 w-4 text-secondary-400" />
                )}
              </div>
              <p className={`mt-1 line-clamp-1 ${TYPE.micro} text-secondary-500 dark:text-secondary-400`}>
                <span className="font-semibold text-secondary-400">{i + 1}.</span> {shot.line || 'Empty shot'}
              </p>
              {url && !previewing && (() => {
                const miss = qualityMiss(shot);
                if (!miss) return null;
                return (
                  <div className={`mt-0.5 ${TYPE.micro}`} data-testid={`preview-quality-miss-${i + 1}`}>
                    {/* Its own line, wrapping to two: at panel width a one-line label truncated to "May not l...". */}
                    <Tooltip content={miss.detail}>
                      <span className="flex items-start gap-1 text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden />
                        <span className="line-clamp-2">{miss.label}</span>
                      </span>
                    </Tooltip>
                    {onRedraw && (
                      <button
                        type="button"
                        onClick={() => onRedraw(i)}
                        disabled={disabled}
                        aria-label={`Redraw panel ${i + 1}`}
                        className="mt-0.5 inline-flex items-center gap-0.5 rounded px-1 text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800 disabled:opacity-50"
                      >
                        <RefreshCw className="h-3 w-3" aria-hidden /> Redraw
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PreviewStrip;
